<template>
  <div class="agents-container">
    <!-- Top Navigation Bar -->
    <nav class="top-nav">
      <div class="nav-content">
        <div class="nav-left">
          <div class="logo">
            <el-icon :size="28" color="#409EFF"><VideoPlay /></el-icon>
            <span class="logo-text">AI短剧平台 · 智能体中心</span>
          </div>
          <el-menu mode="horizontal" :default-active="activeMenu" class="nav-menu" background-color="transparent">
            <el-menu-item index="dashboard" @click="$router.push('/dashboard')">
              <el-icon><HomeFilled /></el-icon>
              项目列表
            </el-menu-item>
            <el-menu-item index="workflow" @click="$router.push('/dashboard')">
              <el-icon><Connection /></el-icon>
              待处理项目
            </el-menu-item>
          </el-menu>
        </div>
        <div class="nav-right">
          <el-dropdown trigger="click" @command="handleUserCommand">
            <div class="user-info">
              <el-avatar :size="36" :src="userStore.userInfo?.avatarUrl" class="user-avatar">
                {{ userStore.userInfo?.username?.charAt(0).toUpperCase() }}
              </el-avatar>
              <span class="username">{{ userStore.userInfo?.username || '用户' }}</span>
              <el-icon class="arrow"><ArrowDown /></el-icon>
            </div>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="logout">
                  <el-icon><SwitchButton /></el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </nav>

    <!-- Main Content -->
    <main class="main-content">
      <div class="content-wrapper">
        <!-- Header Section -->
        <section class="header-section">
          <div class="header-content">
            <h1 class="page-title">多智能体协作系统</h1>
            <p class="page-subtitle">管理和监控9 个专业化 AI智能体，协同完成短剧创作全流程</p>
          </div>
          <div class="header-stats">
            <el-statistic title="可用智能体" :value="availableCount" />
            <el-statistic title="工作中" :value="busyCount" />
            <el-statistic title="总任务数" :value="totalTasks" />
          </div>
        </section>

        <!-- Agents Grid -->
        <section class="agents-grid">
          <!-- Orchestrator Card -->
          <div class="agent-card agent-card--orchestrator" @click="navigateToAgent('orchestrator')">
            <div class="card-header">
              <div class="agent-icon">
                <el-icon :size="40"><Operation /></el-icon>
              </div>
              <el-tag type="warning" size="small" effect="dark">核心</el-tag>
            </div>
            <div class="card-body">
              <h3 class="agent-name">编排智能体</h3>
              <p class="agent-type">Coordinator · 总指挥</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('orchestrator')" size="small" round>
                  {{ getAgentStatusLabel('orchestrator') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">完成任务</span>
                  <span class="value">{{ agents.orchestrator?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">当前项目</span>
                  <span class="value">{{ agents.orchestrator?.current_project_id || '-' }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-01: Demand Analysis -->
          <div class="agent-card" @click="navigateToAgent('demand_analysis')">
            <div class="card-header">
              <div class="agent-icon agent-icon--blue">
                <el-icon :size="32"><Search /></el-icon>
              </div>
              <span class="agent-badge">AI-01</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">需求分析智能体</h3>
              <p class="agent-type">Market Analyst · 市场分析师</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('demand_analysis')" size="small" round>
                  {{ getAgentStatusLabel('demand_analysis') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">分析报告</span>
                  <span class="value">{{ agents.demand_analysis?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">准确率</span>
                  <span class="value">95%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-02: Script Generation -->
          <div class="agent-card" @click="navigateToAgent('script_generation')">
            <div class="card-header">
              <div class="agent-icon agent-icon--purple">
                <el-icon :size="32"><EditPen /></el-icon>
              </div>
              <span class="agent-badge">AI-02</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">剧本创作智能体</h3>
              <p class="agent-type">Screenwriter · 编剧</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('script_generation')" size="small" round>
                  {{ getAgentStatusLabel('script_generation') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已创作剧本</span>
                  <span class="value">{{ agents.script_generation?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">平均时长</span>
                  <span class="value">2h</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-03: Script Review -->
          <div class="agent-card" @click="navigateToAgent('script_review')">
            <div class="card-header">
              <div class="agent-icon agent-icon--orange">
                <el-icon :size="32"><DocumentChecked /></el-icon>
              </div>
              <span class="agent-badge">AI-03</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">剧本审核智能体</h3>
              <p class="agent-type">Editor · 编辑</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('script_review')" size="small" round>
                  {{ getAgentStatusLabel('script_review') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已审核</span>
                  <span class="value">{{ agents.script_review?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">通过率</span>
                  <span class="value">87%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-04: Scene Breakdown -->
          <div class="agent-card" @click="navigateToAgent('scene_breakdown')">
            <div class="card-header">
              <div class="agent-icon agent-icon--cyan">
                <el-icon :size="32"><Grid /></el-icon>
              </div>
              <span class="agent-badge">AI-04</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">场景分解智能体</h3>
              <p class="agent-type">Director · 导演</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('scene_breakdown')" size="small" round>
                  {{ getAgentStatusLabel('scene_breakdown') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已分解场景</span>
                  <span class="value">{{ agents.scene_breakdown?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">平均场景数</span>
                  <span class="value">12</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-05: Character Design -->
          <div class="agent-card" @click="navigateToAgent('character_design')">
            <div class="card-header">
              <div class="agent-icon agent-icon--pink">
                <el-icon :size="32"><User /></el-icon>
              </div>
              <span class="agent-badge">AI-05</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">角色设计智能体</h3>
              <p class="agent-type">Artist · 艺术家</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('character_design')" size="small" round>
                  {{ getAgentStatusLabel('character_design') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已设计角色</span>
                  <span class="value">{{ agents.character_design?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">风格</span>
                  <span class="value">写实/动漫</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-06: Background Generation -->
          <div class="agent-card" @click="navigateToAgent('background_generation')">
            <div class="card-header">
              <div class="agent-icon agent-icon--green">
                <el-icon :size="32"><PictureFilled /></el-icon>
              </div>
              <span class="agent-badge">AI-06</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">背景生成智能体</h3>
              <p class="agent-type">Designer · 设计师</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('background_generation')" size="small" round>
                  {{ getAgentStatusLabel('background_generation') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已生成背景</span>
                  <span class="value">{{ agents.background_generation?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">分辨率</span>
                  <span class="value">4K</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-07: Video Synthesis -->
          <div class="agent-card" @click="navigateToAgent('video_synthesis')">
            <div class="card-header">
              <div class="agent-icon agent-icon--red">
                <el-icon :size="32"><VideoCamera /></el-icon>
              </div>
              <span class="agent-badge">AI-07</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">视频合成智能体</h3>
              <p class="agent-type">Editor · 剪辑师</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('video_synthesis')" size="small" round>
                  {{ getAgentStatusLabel('video_synthesis') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已合成视频</span>
                  <span class="value">{{ agents.video_synthesis?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">平均时长</span>
                  <span class="value">30min</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-08: Audio Generation -->
          <div class="agent-card" @click="navigateToAgent('audio_generation')">
            <div class="card-header">
              <div class="agent-icon agent-icon--indigo">
                <el-icon :size="32"><Headset /></el-icon>
              </div>
              <span class="agent-badge">AI-08</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">音频生成智能体</h3>
              <p class="agent-type">Engineer · 工程师</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('audio_generation')" size="small" round>
                  {{ getAgentStatusLabel('audio_generation') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已生成音频</span>
                  <span class="value">{{ agents.audio_generation?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">音质</span>
                  <span class="value">无损</span>
                </div>
              </div>
            </div>
          </div>

          <!-- AI-09: Compliance Check -->
          <div class="agent-card" @click="navigateToAgent('compliance_check')">
            <div class="card-header">
              <div class="agent-icon agent-icon--gold">
                <el-icon :size="32"><Checked /></el-icon>
              </div>
              <span class="agent-badge">AI-09</span>
            </div>
            <div class="card-body">
              <h3 class="agent-name">合规审查智能体</h3>
              <p class="agent-type">Auditor · 审核员</p>
              <div class="agent-status">
                <el-tag :type="getAgentStatusType('compliance_check')" size="small" round>
                  {{ getAgentStatusLabel('compliance_check') }}
                </el-tag>
              </div>
              <div class="agent-info">
                <div class="info-item">
                  <span class="label">已审查</span>
                  <span class="value">{{ agents.compliance_check?.tasks_completed || 0 }}</span>
                </div>
                <div class="info-item">
                  <span class="label">合规率</span>
                  <span class="value">99%</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Workflow Diagram -->
        <section class="workflow-section">
          <h2 class="section-title">工作流示意图</h2>
          <div class="workflow-diagram">
            <div class="workflow-stage">
              <div class="stage-number">1</div>
              <div class="stage-content">
                <el-icon><Search /></el-icon>
                <span>需求分析</span>
              </div>
            </div>
            <el-icon class="arrow-right"><ArrowRight /></el-icon>
            <div class="workflow-stage">
              <div class="stage-number">2</div>
              <div class="stage-content">
                <el-icon><EditPen /></el-icon>
                <span>剧本创作</span>
              </div>
            </div>
            <el-icon class="arrow-right"><ArrowRight /></el-icon>
            <div class="workflow-stage">
              <div class="stage-number">3</div>
              <div class="stage-content">
                <el-icon><DocumentChecked /></el-icon>
                <span>剧本审核</span>
              </div>
            </div>
            <el-icon class="arrow-right"><ArrowRight /></el-icon>
            <div class="workflow-stage">
              <div class="stage-number">4</div>
              <div class="stage-content">
                <el-icon><Grid /></el-icon>
                <span>场景分解</span>
              </div>
            </div>
            <el-icon class="arrow-right"><ArrowRight /></el-icon>
            <div class="workflow-stage">
              <div class="stage-number">5</div>
              <div class="stage-content">
                <div class="stage-sub">
                  <el-icon><User /></el-icon>
                  <el-icon><PictureFilled /></el-icon>
                </div>
                <span>视觉制作</span>
              </div>
            </div>
            <el-icon class="arrow-right"><ArrowRight /></el-icon>
            <div class="workflow-stage">
              <div class="stage-number">6</div>
              <div class="stage-content">
                <div class="stage-sub">
                  <el-icon><VideoCamera /></el-icon>
                  <el-icon><Headset /></el-icon>
                </div>
                <span>音视频合成</span>
              </div>
            </div>
            <el-icon class="arrow-right"><ArrowRight /></el-icon>
            <div class="workflow-stage stage-final">
              <div class="stage-number">7</div>
              <div class="stage-content">
                <el-icon><Checked /></el-icon>
                <span>合规审查</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { agentsApi } from '@/api/agents'
import {
  VideoPlay,
  HomeFilled,
  Connection,
  ArrowDown,
  SwitchButton,
  Operation,
  Search,
  EditPen,
  DocumentChecked,
  Grid,
  User,
  PictureFilled,
  VideoCamera,
  Headset,
  Checked,
  ArrowRight,
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import type { AgentInfo, AgentListResponse } from '@/types/workflow'

const router = useRouter()
const userStore = useUserStore()

const activeMenu = ref('agents')
const loading = ref(false)

// Agent data structure
const agents = reactive<Record<string, AgentInfo>>({
  orchestrator: null as any,
  demand_analysis: null as any,
  script_generation: null as any,
  script_review: null as any,
  scene_breakdown: null as any,
  character_design: null as any,
  background_generation: null as any,
  video_synthesis: null as any,
  audio_generation: null as any,
  compliance_check: null as any,
})

const agentList = ref<AgentListResponse>({
  agents: [],
  total: 0,
  available: 0,
  busy: 0,
})

// Computed properties
const availableCount = computed(() => agentList.value.available)
const busyCount = computed(() => agentList.value.busy)
const totalTasks = computed(() => {
  return Object.values(agents).reduce((sum, agent) => {
    return sum + (agent?.tasks_completed || 0)
  }, 0)
})

// Methods
const navigateToAgent = (agentName: string) => {
  router.push(`/agent/${agentName}`)
}

const getAgentStatusType = (agentName: string) => {
  const agent = agents[agentName]
  if (!agent) return 'info'
  if (!agent.is_available) return 'danger'
  if (agent.current_task_id) return 'warning'
  return 'success'
}

const getAgentStatusLabel = (agentName: string) => {
  const agent = agents[agentName]
  if (!agent) return 'Unknown'
  if (!agent.is_available) return '离线'
  if (agent.current_task_id) return '工作中'
  return '空闲'
}

const handleUserCommand = (command: string) => {
  if (command === 'logout') {
    userStore.logout()
    router.push('/login')
  }
}

const loadAgents = async () => {
  loading.value = true
  try {
    const response = await agentsApi.getList()
    agentList.value = response
    
    // Map agents by name
    response.agents.forEach(agent => {
      agents[agent.agent_name] = agent
    })
  } catch (error) {
    console.error('Failed to load agents:', error)
    ElMessage.error('加载智能体状态失败')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadAgents()
})
</script>

<style scoped lang="scss">
.agents-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.top-nav {
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.95);
  
  .nav-content {
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 70px;
  }
  
  .nav-left {
    display: flex;
    align-items: center;
    gap: 32px;
  }
  
  .logo {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .logo-text {
      font-size: 20px;
      font-weight: 700;
      color: #303133;
    }
  }
  
  .nav-menu {
    border: none;
    background: transparent !important;
    
    :deep(.el-menu-item) {
      border: none;
      color: #606266;
      
      &.is-active {
        color: #667eea;
        background: rgba(102, 126, 234, 0.1) !important;
      }
      
      &:hover {
        background: rgba(102, 126, 234, 0.05);
      }
    }
  }
  
  .nav-right {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  
  .user-info {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 6px 12px;
    border-radius: 20px;
    
    &:hover {
      background: rgba(102, 126, 234, 0.1);
    }
    
    .username {
      font-size: 14px;
      color: #606266;
    }
    
    .arrow {
      font-size: 12px;
      color: #909399;
    }
  }
}

.main-content {
  padding: 32px 24px;
  max-width: 1600px;
  margin: 0 auto;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  .header-content {
    .page-title {
      font-size: 32px;
      font-weight: 700;
      color: #303133;
      margin: 0 0 8px 0;
    }
    
    .page-subtitle {
      font-size: 16px;
      color: #909399;
      margin: 0;
    }
  }
  
  .header-stats {
    display: flex;
    gap: 48px;
  }
}

.agents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  
  .agent-card {
    background: white;
    border-radius: 16px;
    padding: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    
    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 24px rgba(102, 126, 234, 0.2);
    }
    
    &--orchestrator {
      grid-column: span 2;
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      color: white;
      
      .agent-name, .agent-type, .info-item .label, .info-item .value {
        color: white;
      }
    }
    
    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      
      .agent-icon {
        width: 60px;
        height: 60px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(102, 126, 234, 0.1);
        color: #667eea;
        
        &--blue {
          background: rgba(64, 158, 255, 0.1);
          color: #409EFF;
        }
        
        &--purple {
          background: rgba(147, 51, 234, 0.1);
          color: #9333EA;
        }
        
        &--orange {
          background: rgba(230, 162, 60, 0.1);
          color: #E6A23C;
        }
        
        &--cyan {
          background: rgba(6, 182, 212, 0.1);
          color: #06B6D4;
        }
        
        &--pink {
          background: rgba(236, 72, 153, 0.1);
          color: #EC4899;
        }
        
        &--green {
          background: rgba(34, 197, 94, 0.1);
          color: #22C55E;
        }
        
        &--red {
          background: rgba(239, 68, 68, 0.1);
          color: #EF4444;
        }
        
        &--indigo {
          background: rgba(99, 102, 241, 0.1);
          color: #6366F1;
        }
        
        &--gold {
          background: rgba(234, 179, 8, 0.1);
          color: #EAB308;
        }
      }
      
      .agent-badge {
        padding: 4px 12px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
      }
    }
    
    .card-body {
      .agent-name {
        font-size: 18px;
        font-weight: 600;
        color: #303133;
        margin: 0 0 4px 0;
      }
      
      .agent-type {
        font-size: 13px;
        color: #909399;
        margin: 0 0 12px 0;
      }
      
      .agent-status {
        margin-bottom: 16px;
      }
      
      .agent-info {
        display: flex;
        justify-content: space-between;
        
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          
          .label {
            font-size: 12px;
            color: #909399;
          }
          
          .value {
            font-size: 16px;
            font-weight: 600;
            color: #303133;
          }
        }
      }
    }
  }
}

.workflow-section {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  .section-title {
    font-size: 24px;
    font-weight: 700;
    color: #303133;
    margin: 0 0 32px 0;
  }
  
  .workflow-diagram {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    
    .workflow-stage {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      
      .stage-number {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 14px;
      }
      
      .stage-content {
        padding: 16px 20px;
        background: #f5f7fa;
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: #606266;
        font-weight: 500;
        
        .stage-sub {
          display: flex;
          gap: 8px;
        }
      }
      
      &.stage-final .stage-content {
        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
        color: white;
      }
    }
    
    .arrow-right {
      font-size: 24px;
      color: #909399;
    }
  }
}
</style>
